"use client";
import { useEffect, useState } from 'react';
import Image from 'next/image';
import '@/styles/globals.css';

export default function MaintenancePage() {
  const [animationData, setAnimationData] = useState(null);
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [showTimer, setShowTimer] = useState(false);
  const [endTimeParam, setEndTimeParam] = useState(null);

  useEffect(() => {
    // Mark this page as a maintenance page to hide chatbot
    document.body.setAttribute('data-page-type', 'maintenance');

    // Get end time from URL query parameter
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const endTime = params.get('endTime');
      console.log('End time from URL:', endTime);
      
      if (endTime) {
        // Validate the date
        const parsedDate = new Date(endTime);
        if (!isNaN(parsedDate.getTime())) {
          setEndTimeParam(endTime);
          setShowTimer(true);
          console.log('Timer enabled with end time:', parsedDate.toLocaleString());
        } else {
          console.error('Invalid date format:', endTime);
        }
      } else {
        console.log('No endTime parameter found in URL');
      }
    }

    // Try to load a maintenance animation, fallback to a simple design
    fetch('/404.json')
      .then(res => res.json())
      .then(data => setAnimationData(data))
      .catch(err => {
        console.log('Animation not found, using fallback design');
        setAnimationData(null);
      });
  }, []);

  useEffect(() => {
    // Set up countdown timer if end time is provided
    if (!endTimeParam) return;

    const endTime = new Date(endTimeParam).getTime();
    
    // Validate end time
    if (isNaN(endTime)) {
      console.error('Invalid end time:', endTimeParam);
      return;
    }

    const updateTimer = () => {
      const now = new Date().getTime();
      const difference = endTime - now;

      if (difference > 0) {
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((difference % (1000 * 60)) / 1000);

        setTimeLeft({ days, hours, minutes, seconds });
      } else {
        // Time has passed
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }
    };

    // Initial call to set timer immediately
    updateTimer();
    
    // Update every second
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [endTimeParam]);

  return (
    <>
      <style dangerouslySetInnerHTML={{
        __html: `
          body {
            margin: 0;
            padding: 0;
            overflow: hidden;
          }
        `
      }} />
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      
      <div className="container mx-auto px-4 py-8 md:py-12 text-center">
        <div className="max-w-3xl mx-auto">
          {/* Logo */}
          <div className="mb-4 md:mb-6 flex justify-center">
            <Image 
              src="/images/image.png" 
              alt="Hyrelancer Logo" 
              width={120}
              height={60}
              className="h-10 md:h-14 w-auto object-contain"
              priority
            />
          </div>

          {/* Content */}
          <div className="text-white">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-3 md:mb-4 bg-gradient-to-r from-[#3a599c] to-purple-500 bg-clip-text text-transparent">
              We&apos;ll Be Back Soon!
            </h1>
            <p className="text-base md:text-lg lg:text-xl text-slate-300 mb-2 md:mb-3">
              Our site is currently under maintenance
            </p>
            <p className="text-sm md:text-base text-slate-400 mb-6 md:mb-8 max-w-xl mx-auto">
              We&apos;re working hard to improve your experience. We&apos;ll be back online shortly.
              Thank you for your patience!
            </p>

            {/* Countdown Timer */}
            {showTimer && endTimeParam && (
              <div className="mb-6 md:mb-8">
                <p className="text-base md:text-lg text-slate-200 mb-3 md:mb-4 font-semibold">We&apos;ll be back in:</p>
                <div className="flex justify-center gap-2 sm:gap-3 md:gap-4 flex-wrap mb-2 md:mb-3">
                  {/* Days */}
                  <div className="bg-gradient-to-br from-slate-800 to-slate-900 backdrop-blur-sm rounded-lg p-2 md:p-3 lg:p-4 border border-blue-500/30 min-w-[60px] sm:min-w-[70px] md:min-w-[80px] shadow-md hover:border-blue-400/50 transition-all">
                    <div className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-blue-400 mb-1 drop-shadow-lg">
                      {String(timeLeft.days).padStart(2, '0')}
                    </div>
                    <div className="text-xs sm:text-sm text-slate-300 uppercase tracking-wide font-medium">Days</div>
                  </div>
                  
                  {/* Hours */}
                  <div className="bg-gradient-to-br from-slate-800 to-slate-900 backdrop-blur-sm rounded-lg p-2 md:p-3 lg:p-4 border border-blue-500/30 min-w-[60px] sm:min-w-[70px] md:min-w-[80px] shadow-md hover:border-blue-400/50 transition-all">
                    <div className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-blue-400 mb-1 drop-shadow-lg">
                      {String(timeLeft.hours).padStart(2, '0')}
                    </div>
                    <div className="text-xs sm:text-sm text-slate-300 uppercase tracking-wide font-medium">Hours</div>
                  </div>
                  
                  {/* Minutes */}
                  <div className="bg-gradient-to-br from-slate-800 to-slate-900 backdrop-blur-sm rounded-lg p-2 md:p-3 lg:p-4 border border-blue-500/30 min-w-[60px] sm:min-w-[70px] md:min-w-[80px] shadow-md hover:border-blue-400/50 transition-all">
                    <div className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-blue-400 mb-1 drop-shadow-lg">
                      {String(timeLeft.minutes).padStart(2, '0')}
                    </div>
                    <div className="text-xs sm:text-sm text-slate-300 uppercase tracking-wide font-medium">Minutes</div>
                  </div>
                  
                  {/* Seconds */}
                  <div className="bg-gradient-to-br from-slate-800 to-slate-900 backdrop-blur-sm rounded-lg p-2 md:p-3 lg:p-4 border border-blue-500/30 min-w-[60px] sm:min-w-[70px] md:min-w-[80px] shadow-md hover:border-blue-400/50 transition-all animate-pulse">
                    <div className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-blue-400 mb-1 drop-shadow-lg">
                      {String(timeLeft.seconds).padStart(2, '0')}
                    </div>
                    <div className="text-xs sm:text-sm text-slate-300 uppercase tracking-wide font-medium">Seconds</div>
                  </div>
                </div>
              </div>
            )}

            {/* Features/Info Cards */}
            <div className="grid md:grid-cols-3 gap-4 md:gap-6 mt-6 md:mt-8 mb-6">
              <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg p-4 md:p-5 border border-slate-700">
                <div className="text-2xl md:text-3xl mb-2">🚀</div>
                <h3 className="text-base md:text-lg font-semibold mb-1">Upgrading</h3>
                <p className="text-slate-400 text-xs md:text-sm">
                  We&apos;re upgrading our systems for better performance
                </p>
              </div>
              <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg p-4 md:p-5 border border-slate-700">
                <div className="text-2xl md:text-3xl mb-2">⚡</div>
                <h3 className="text-base md:text-lg font-semibold mb-1">Improving</h3>
                <p className="text-slate-400 text-xs md:text-sm">
                  Enhancing features to serve you better
                </p>
              </div>
              <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg p-4 md:p-5 border border-slate-700">
                <div className="text-2xl md:text-3xl mb-2">🔒</div>
                <h3 className="text-base md:text-lg font-semibold mb-1">Securing</h3>
                <p className="text-slate-400 text-xs md:text-sm">
                  Strengthening security measures
                </p>
              </div>
            </div>

            {/* Contact Info */}
            <div className="mt-6 md:mt-8 pt-6 md:pt-8 border-t border-slate-700">
              <p className="text-sm md:text-base text-slate-400">
                Need immediate assistance? Contact us at{' '}
                <a href="mailto:support@hyrelancer.com" className="text-blue-400 hover:text-blue-300 underline">
                  support@hyrelancer.com
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Animated Background Elements */}
      <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl"></div>
      </div>
    </div>
    </>
  );
}


